import {
  DocxEditor,
  DOCX_MIME_TYPE,
  useDocxTemplateFieldActions,
  type DocxEditorHandle,
} from "@/components/features/matter/file-repo/DocxEditor";
import { ActionConfirmationModal, Button, SheetFooter } from "@/components/ui";
import { AppModal } from "@/components/ui/AppModal/AppModal";
import { FormLoadingOverlay } from "@/components/ui/Form/FormLoadingOverlay";
import { useUpdateMatterTemplate } from "@/lib/data/react-query/matters/matter-template";
import { generateS3ItemPreSignedUrlV1 } from "@/lib/data/requests/s3";
import { VITE_APRYSE_LICENSE_KEY } from "@/lib/data/static/importEnv";
import type { MatterTemplate } from "@/lib/types";
import {
  ensureFileNameHasExtension,
  getFileNameParts,
} from "@/lib/utils/matterFile";
import { useEffect, useMemo, useRef, useState } from "react";
import { XIcon } from "lucide-react";
import toast from "react-hot-toast";
import { TemplateFieldsDialog } from "./TemplateFieldsDialog";

interface EditDocxTemplateModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  template: MatterTemplate;
  onSaved?: () => void;
  templateFieldScope?: "all" | "signers-only";
}

const normalizeDocxFileName = (name: string) => {
  const trimmed = name.trim();
  if (!trimmed) return "template.docx";
  return ensureFileNameHasExtension(trimmed, "docx");
};

export const EditDocxTemplateModal = ({
  open,
  setOpen,
  template,
  onSaved,
  templateFieldScope = "all",
}: EditDocxTemplateModalProps) => {
  const updateMatterTemplate = useUpdateMatterTemplate();
  const editorRef = useRef<DocxEditorHandle | null>(null);
  const {
    templateFieldsTriggerRef,
    handleInsertAtCursor,
    handleOpenTemplateFields,
  } = useDocxTemplateFieldActions(editorRef);
  const [isEditorReady, setIsEditorReady] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);

  const resolvedFileName = useMemo(() => {
    if (template.name) {
      return normalizeDocxFileName(template.name);
    }
    const fallbackKey = template.presignedUrlKey;
    if (!fallbackKey) return "template.docx";
    const { fileName, fileExtension } = getFileNameParts(fallbackKey);
    if (fileExtension) {
      return normalizeDocxFileName(`${fileName}.${fileExtension}`);
    }
    return normalizeDocxFileName(fileName);
  }, [template.name, template.presignedUrlKey]);

  useEffect(() => {
    if (!open) {
      setIsEditorReady(false);
      setIsExporting(false);
      setConfirmClose(false);
    }
  }, [open, template.id]);

  const handleSave = async () => {
    if (!editorRef.current) {
      toast.error("Document editor is not ready yet.");
      return;
    }

    setIsExporting(true);
    try {
      const blob = await editorRef.current.exportDocument();
      if (!blob) {
        toast.error("Unable to export the document.");
        return;
      }

      const presignedURLRes = await generateS3ItemPreSignedUrlV1(
        "matter-template",
      )({
        clientMethod: "put_object",
        fileName: resolvedFileName,
      });

      await fetch(presignedURLRes.data.presignedUrl, {
        method: "PUT",
        headers: {
          "Content-Type": blob.type || DOCX_MIME_TYPE,
        },
        body: blob,
      });

      await updateMatterTemplate.mutateAsync({
        id: template.id,
        dateModified: template.dateModified,
        presignedUrlKey: presignedURLRes.data.key,
      });

      setOpen(false);
      onSaved?.();
    } catch (error) {
      console.error("Failed to save template document", error);
      toast.error("Failed to save document");
    } finally {
      setIsExporting(false);
    }
  };

  const isSaving = updateMatterTemplate.isPending || isExporting;
  const allowedTemplateFieldParents =
    templateFieldScope === "signers-only"
      ? (["signer", "userSigner"] as const)
      : undefined;

  return (
    <AppModal
      open={open}
      onOpenChange={setOpen}
      isDirty
      hideHeader
      hideCloseButton
      overlayClassName="z-[108] bg-black/50 backdrop-blur-md"
      title={`Edit ${template.name || "template"}`}
      description="Save changes to this template."
      bodyContained
      className="top-0 left-0 z-[109] h-[100dvh] !max-h-[100dvh] !w-screen !max-w-none translate-x-0 translate-y-0 rounded-none"
    >
      <div className="relative flex min-h-0 grow flex-col overflow-hidden">
        {isSaving && <FormLoadingOverlay />}
        <div className="flex min-h-0 flex-1 flex-col px-4 py-4">
          <DocxEditor
            key={`docx-template-${template.id}`}
            ref={editorRef}
            fileName={resolvedFileName}
            presignedUrlKey={template.presignedUrlKey || ""}
            fileType="matter-template"
            licenseKey={VITE_APRYSE_LICENSE_KEY}
            onTemplateFieldsRequest={handleOpenTemplateFields}
            onReadyChange={setIsEditorReady}
            isActive={open}
            showSaveButton={false}
            showHeader
            headerActions={
              <div className="flex items-center gap-2">
                <TemplateFieldsDialog
                  hideTrigger
                  onInsertAtCursor={handleInsertAtCursor}
                  triggerLabel="Template variables"
                  triggerRef={templateFieldsTriggerRef}
                  allowedParents={allowedTemplateFieldParents}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8 w-8 p-0"
                  onClick={() => {
                    setConfirmClose(true);
                  }}
                >
                  <XIcon className="h-4 w-4" />
                  <span className="sr-only">Close</span>
                </Button>
              </div>
            }
            className="min-h-0 flex-1"
          />
        </div>
        <SheetFooter className="mt-auto shrink-0 border-t px-6 py-4">
          <div className="flex grow justify-end gap-x-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setConfirmClose(true);
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="light-primary"
              onClick={() => {
                void handleSave();
              }}
              disabled={!isEditorReady || isSaving}
            >
              {isSaving ? "Saving..." : "Save"}
            </Button>
          </div>
        </SheetFooter>
      </div>
      <ActionConfirmationModal
        open={confirmClose}
        setOpen={setConfirmClose}
        title="Unsaved changes"
        buttonText="Confirm"
        description="You have unsaved changes. Are you sure you want to close?"
        afterActionFn={() => {
          setOpen(false);
          setConfirmClose(false);
        }}
      />
    </AppModal>
  );
};
